export const getCookieValue = (
    name: string,
    cookies?: string | null,
): string | null => {
    const source =
        cookies ?? (typeof document === 'undefined' ? null : document.cookie);

    if (!source) {
        return null;
    }

    const match = source.split('; ').find((row) => row.startsWith(`${name}=`));

    if (!match) {
        return null;
    }

    return decodeURIComponent(match.split('=').slice(1).join('='));
};

export const getUrlParam = (
    param: string,
    search?: string | null,
): string | null => {
    const source =
        search ??
        (typeof window === 'undefined' ? null : window.location.search);

    if (!source) {
        return null;
    }

    return new URLSearchParams(source).get(param);
};

type MetaPixelContext = {
    search?: string | null;
    cookies?: string | null;
    now?: number;
};

export const getMetaPixelIds = (
    ctx: MetaPixelContext = {},
): { fbp: string | null; fbc: string | null } => {
    const search =
        ctx.search ??
        (typeof window === 'undefined' ? null : window.location.search);
    const cookies =
        ctx.cookies ??
        (typeof document === 'undefined' ? null : document.cookie);
    const now = ctx.now ?? Date.now();

    const fbclid = getUrlParam('fbclid', search);
    const fbp = getCookieValue('_fbp', cookies);
    const existingFbc = getCookieValue('_fbc', cookies);
    const fbc = fbclid ? `fb.1.${now}.${fbclid}` : existingFbc;

    return { fbp, fbc };
};

export const CHECKOUT_META_EVENT_ID_STORAGE_KEY = 'checkoutMetaEventId';
export const CHECKOUT_META_PIXEL_PAYLOAD_STORAGE_KEY =
    'checkoutMetaPixelPayload';
export const CHECKOUT_USER_EMAIL_STORAGE_KEY = 'checkoutUserEmail';
export const CHECKOUT_META_TRIAL_START_PARAM = 'metaTrialStart';

export type CheckoutMetaPixelPayload = {
    currency: string;
    value: number;
    content_type: 'subscription';
    content_name: string;
    content_category: string;
    subscription_interval: string;
};

const getTrialValue = (
    currency: string,
    interval: string,
    productId: string,
): number => {
    const normalizedCurrency = currency.toUpperCase();

    if (productId === 'premium_library') {
        if (interval === 'year') {
            const yearlyByCurrency: Record<string, number> = {
                USD: 107,
                EUR: 107,
                NOK: 1068,
                GBP: 89,
            };

            return yearlyByCurrency[normalizedCurrency] ?? yearlyByCurrency.USD;
        }

        const monthlyByCurrency: Record<string, number> = {
            USD: 14.99,
            EUR: 14.99,
            NOK: 149,
            GBP: 14.99,
        };

        return monthlyByCurrency[normalizedCurrency] ?? monthlyByCurrency.USD;
    }

    return 0;
};

export const buildCheckoutMetaPixelPayload = ({
    product_id = 'premium_library',
    interval = 'month',
    currency = 'USD',
}: {
    product_id?: string;
    interval?: string;
    currency?: string;
}): CheckoutMetaPixelPayload => {
    const normalizedInterval = interval === 'year' ? 'year' : 'month';
    const contentCategory = normalizedInterval;
    const contentName =
        product_id === 'premium_library'
            ? `Membership ${normalizedInterval === 'year' ? 'Year' : 'Month'}`
            : `${product_id} ${normalizedInterval}`;

    return {
        currency: currency.toUpperCase(),
        value: getTrialValue(currency, normalizedInterval, product_id),
        content_type: 'subscription',
        content_name: contentName,
        content_category: contentCategory,
        subscription_interval: normalizedInterval,
    };
};

export const persistCheckoutMetaEventId = (
    metaEventId?: string | null,
): void => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!metaEventId) {
        window.sessionStorage.removeItem(CHECKOUT_META_EVENT_ID_STORAGE_KEY);
        return;
    }

    window.sessionStorage.setItem(
        CHECKOUT_META_EVENT_ID_STORAGE_KEY,
        metaEventId,
    );
};

export const persistCheckoutMetaPixelPayload = (
    payload?: CheckoutMetaPixelPayload | null,
): void => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!payload) {
        window.sessionStorage.removeItem(
            CHECKOUT_META_PIXEL_PAYLOAD_STORAGE_KEY,
        );
        return;
    }

    window.sessionStorage.setItem(
        CHECKOUT_META_PIXEL_PAYLOAD_STORAGE_KEY,
        JSON.stringify(payload),
    );
};

export const consumeCheckoutMetaEventId = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const metaEventId = window.sessionStorage.getItem(
        CHECKOUT_META_EVENT_ID_STORAGE_KEY,
    );

    if (metaEventId) {
        window.sessionStorage.removeItem(CHECKOUT_META_EVENT_ID_STORAGE_KEY);
    }

    return metaEventId;
};

export const consumeCheckoutMetaPixelPayload =
    (): CheckoutMetaPixelPayload | null => {
        if (typeof window === 'undefined') {
            return null;
        }

        const payload = window.sessionStorage.getItem(
            CHECKOUT_META_PIXEL_PAYLOAD_STORAGE_KEY,
        );

        if (payload) {
            window.sessionStorage.removeItem(
                CHECKOUT_META_PIXEL_PAYLOAD_STORAGE_KEY,
            );
        }

        if (!payload) {
            return null;
        }

        try {
            const parsed = JSON.parse(
                payload,
            ) as Partial<CheckoutMetaPixelPayload>;

            if (
                typeof parsed.currency !== 'string' ||
                typeof parsed.value !== 'number' ||
                typeof parsed.content_type !== 'string' ||
                typeof parsed.content_name !== 'string' ||
                typeof parsed.content_category !== 'string' ||
                typeof parsed.subscription_interval !== 'string'
            ) {
                return null;
            }

            return {
                currency: parsed.currency,
                value: parsed.value,
                content_type: 'subscription',
                content_name: parsed.content_name,
                content_category: parsed.content_category,
                subscription_interval: parsed.subscription_interval,
            };
        } catch {
            return null;
        }
    };

export const persistCheckoutUserEmail = (email?: string | null): void => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!email) {
        window.sessionStorage.removeItem(CHECKOUT_USER_EMAIL_STORAGE_KEY);
        return;
    }

    window.sessionStorage.setItem(CHECKOUT_USER_EMAIL_STORAGE_KEY, email);
};

export const consumeCheckoutUserEmail = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const email = window.sessionStorage.getItem(
        CHECKOUT_USER_EMAIL_STORAGE_KEY,
    );

    if (email) {
        window.sessionStorage.removeItem(CHECKOUT_USER_EMAIL_STORAGE_KEY);
    }

    return email;
};

export const hasStoredCheckoutMetaPixelPayload = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    return !!window.sessionStorage.getItem(
        CHECKOUT_META_PIXEL_PAYLOAD_STORAGE_KEY,
    );
};

export const withTrialStartParam = (
    url?: string | null,
): string | undefined => {
    if (!url) {
        return undefined;
    }

    const nextUrl = new URL(
        url,
        typeof window === 'undefined'
            ? 'https://example.com'
            : window.location.origin,
    );
    nextUrl.searchParams.set(CHECKOUT_META_TRIAL_START_PARAM, 'true');
    return nextUrl.toString();
};

export const generateEventId = (
    opts: {
        now?: number;
        random?: () => number;
        uuid?: () => string;
    } = {},
): string => {
    const uuid = opts.uuid?.();

    if (uuid) {
        return uuid;
    }

    const now = opts.now ?? Date.now();
    const random = opts.random ?? Math.random;

    return `ec_${now}_${random().toString(36).slice(2, 10)}`;
};
